import { Box2, MathUtils, Vector2 } from 'three';

import { Vector2KDTree } from './kd-tree-2d';
import { ProfileSectionLineData } from './profile-section-tool-menu';

export type LengthProfileBfsCell = {
  vertex: Vector2;
  size: number;
};

/**
 * Samples points from the segments using max point distance.
 */
function buildKdTreeFromSegments(segments: ProfileSectionLineData[], maxPointDistance: number) {
  const sampledPoints: Vector2[] = [];

  for (const {
    projected: [start, end],
  } of segments) {
    const length = start.distanceTo(end);

    // Push the start point only if it has a non-negligible distance to the last point.
    if (
      sampledPoints.length === 0 ||
      sampledPoints.at(-1)!.distanceToSquared(start) > bfsSearchEpsilon
    ) {
      sampledPoints.push(start.clone());
    }

    if (length < bfsSearchEpsilon) {
      continue;
    }

    const steps = Math.ceil(length / maxPointDistance);
    const step = MathUtils.clamp(1 / steps, 0, 1);

    for (let t = step; t < 1; t += step) {
      sampledPoints.push(start.clone().lerp(end, t));
    }

    sampledPoints.push(end.clone());
  }

  return new Vector2KDTree(sampledPoints);
}

const bfsSearchEpsilon = 10e-8;

/**
 * Max number of steps to travel across the boundary.
 * The higher this value is the more accurate the algortihm becomes.
 * It should be as high a value as possible without creating lag when performing the calculation.
 */
export function calculateStepSize(maxBfsFillIterations: number, boundary: Box2) {
  // Calculates the diagonal of the boundary.
  const diagonal = 2 * Math.sqrt(maxBfsFillIterations);
  // Our filter threshold will be determined from how many steps it takes to traverse the diagonal of the boundary.
  return boundary.min.distanceTo(boundary.max) / diagonal;
}

export function floodFillBfs(
  cursorPosition: Vector2,
  segments: ProfileSectionLineData[],
  boundary: Box2,
  stepSize: number,
): { cells: LengthProfileBfsCell[]; ghostArea: number } {
  // Step size is small enough that it will capture every single point within reach.
  const stepsX = Math.ceil((boundary.max.x - boundary.min.x) / stepSize);
  const stepsY = Math.ceil((boundary.max.y - boundary.min.y) / stepSize);

  const maxSubdivision = 2 ** 4;
  /**
   * Max point distance, it can be more than step size, but small enough that
   * the BFS fill can not step between two points.
   */
  const maxPointDistance = (stepSize / maxSubdivision) * 0.75;
  const minSize = stepSize / maxSubdivision;
  /**
   * Should be big enough so KD tree can find nearby points when tracing polygons in the graph.
   * We will search half the diagonal of each cell, that way we will find all the points within that cell and a bit more.
   */
  const searchRange = stepSize / 2 + minSize / 4;

  const kdTree = buildKdTreeFromSegments(segments, maxPointDistance);

  // Initialize flag grid
  const bfsFlags: Uint8Array[] = Array.from({ length: stepsX }, () => new Uint8Array(stepsY));
  /**
   * Keeps track of which corners are inside of the edges.
   */
  const cornerFlags: Uint8Array[] = Array.from(
    { length: stepsX + 1 },
    () => new Uint8Array(stepsY + 1),
  );

  const cells: LengthProfileBfsCell[] = [];
  const traversedFlag = 1;

  const queue: [number, number][] = [
    [
      Math.floor((cursorPosition.x - boundary.min.x) / stepSize),
      Math.floor((cursorPosition.y - boundary.min.y) / stepSize),
    ],
  ];

  let ghostCells = 0;
  while (queue.length > 0) {
    const [ix, iy] = queue.shift()!;

    // Cancel when out of bounds since we only measure inner areas.
    if (ix < 0 || iy < 0 || ix >= stepsX || iy >= stepsY) {
      return { cells: [], ghostArea: 0 };
    }

    // Skip if traversed.
    if (bfsFlags[ix][iy] === traversedFlag) {
      continue;
    }

    const current: LengthProfileBfsCell = {
      vertex: new Vector2(
        boundary.min.x + ix * stepSize + stepSize / 2,
        boundary.min.y + iy * stepSize + stepSize / 2,
      ),
      size: stepSize,
    };

    bfsFlags[ix][iy] = traversedFlag;

    // Check for proximity to KDTree points (wall detection)
    const nearbyPoints = kdTree.rangeSearchSquare(current.vertex, searchRange);

    // Any cells that are close to the wall will be queued for subdivision.
    if (nearbyPoints.length > 0) {
      // Create a subset of the KD tree with only the relevant points. Since we need to
      // do many additional checks when we subdivide this is a lot more efficient than passing
      // the original KD tree which can be quite large.
      const kdTreeSubset = new Vector2KDTree(nearbyPoints);
      const subDivisionResult = subdivideCellRecursive(current, kdTreeSubset, minSize, [
        cornerFlags[ix][iy] === traversedFlag,
        cornerFlags[ix + 1][iy] === traversedFlag,
        cornerFlags[ix][iy + 1] === traversedFlag,
        cornerFlags[ix + 1][iy + 1] === traversedFlag,
      ]);

      ghostCells += subDivisionResult.ghostCells;
      cells.push(...subDivisionResult.cells);

      // Partial update of corners.
      cornerFlags[ix][iy] = subDivisionResult.subCorners[0][0];
      cornerFlags[ix + 1][iy] = subDivisionResult.subCorners[2][0];
      cornerFlags[ix][iy + 1] = subDivisionResult.subCorners[0][2];
      cornerFlags[ix + 1][iy + 1] = subDivisionResult.subCorners[2][2];
    } else {
      cells.push(current);

      // Mark each corner as traversed
      cornerFlags[ix][iy] = traversedFlag;
      cornerFlags[ix + 1][iy] = traversedFlag;
      cornerFlags[ix][iy + 1] = traversedFlag;
      cornerFlags[ix + 1][iy + 1] = traversedFlag;

      // Push neighbors
      queue.push([ix - 1, iy]);
      queue.push([ix + 1, iy]);
      queue.push([ix, iy - 1]);
      queue.push([ix, iy + 1]);

      // Push diagonals
      queue.push([ix - 1, iy - 1]);
      queue.push([ix - 1, iy + 1]);
      queue.push([ix + 1, iy - 1]);
      queue.push([ix + 1, iy + 1]);
    }
  }

  return {
    cells,
    ghostArea: ghostCells * minSize ** 2,
  };
}

type ValidCornersBLBRTLTR = [boolean, boolean, boolean, boolean];

type Quadrant = {
  center: Vector2;
  isNearWall: boolean;
  dx: 0 | 1;
  dy: 0 | 1;
  processed: boolean;
  subdivided: boolean;
};

function getQuadrant(
  cell: LengthProfileBfsCell,
  kdTree: Vector2KDTree,
  searchRange: number,
  dx: 0 | 1,
  dy: 0 | 1,
): Quadrant {
  const offsetX = dx === 1 ? 0.25 : -0.25;
  const offsetY = dy === 1 ? 0.25 : -0.25;

  const center = new Vector2(
    cell.vertex.x + offsetX * cell.size,
    cell.vertex.y + offsetY * cell.size,
  );

  return {
    center,
    isNearWall: kdTree.rangeSearchSquare(center, searchRange).length > 0,
    dx,
    dy,
    processed: false,
    subdivided: false,
  };
}

/**
 * Subdivides the cell and grows towards the wall until min size is hit.
 *
 * The valid corners indicates which corners of the cell that are still inside of the boundary.
 * A corner with false is outside of the edge.
 */
function subdivideCellRecursive(
  cell: LengthProfileBfsCell,
  kdTree: Vector2KDTree,
  minSize: number,
  validCornersBLBRTLTR: ValidCornersBLBRTLTR,
): { cells: LengthProfileBfsCell[]; subCorners: Uint8Array[]; ghostCells: number } {
  let ghostCells = 0;
  // The cell is divided into 9 sub-corners. Each quadrant has 4 corners each.
  // Here we need to keep track of which sub-corners we know to be within the edges.
  const subCorners = Array.from({ length: 3 }, () => new Uint8Array(3));

  // Skip invalid cells to avoid subdividing into oblivion.
  if (!validCornersBLBRTLTR.some((x) => x === true)) {
    return { cells: [], subCorners, ghostCells };
  }

  // In order: Bottom left, bottom right, Top left, Top right
  subCorners[0][0] = Number(validCornersBLBRTLTR[0]);
  subCorners[2][0] = Number(validCornersBLBRTLTR[1]);
  subCorners[0][2] = Number(validCornersBLBRTLTR[2]);
  subCorners[2][2] = Number(validCornersBLBRTLTR[3]);

  // Inclusive strategy for min cells.
  // if(cell.size <= minSize) {
  //   return { cells: [cell], subCorners };
  // }

  const result: LengthProfileBfsCell[] = [];
  const halfSize = cell.size / 2;
  const searchRange = halfSize + minSize / 4;

  // For each quadrant, define which parent edges it inherits:
  const quadrants: Quadrant[] = [
    getQuadrant(cell, kdTree, searchRange, 0, 0),
    getQuadrant(cell, kdTree, searchRange, 1, 0),
    getQuadrant(cell, kdTree, searchRange, 0, 1),
    getQuadrant(cell, kdTree, searchRange, 1, 1),
  ];

  let quadrantAddedOrSubdivided = true;
  while (quadrantAddedOrSubdivided) {
    quadrantAddedOrSubdivided = false;

    // Check for quadrants to add.
    for (const quadrant of quadrants) {
      // Skip quadrant if it's already marked as processed.
      if (quadrant.processed) {
        continue;
      }

      // If the corner related to the quadrant is interior, then we can process it.
      const validQuadrant =
        subCorners[quadrant.dx][quadrant.dy] ||
        subCorners[quadrant.dx + 1][quadrant.dy] ||
        subCorners[quadrant.dx][quadrant.dy + 1] ||
        subCorners[quadrant.dx + 1][quadrant.dy + 1];

      if (validQuadrant) {
        if (!quadrant.isNearWall) {
          // Safe cell, keep as is.
          result.push({
            vertex: quadrant.center,
            size: halfSize,
          });

          // Since this quadrant have no nearby walls, we can consider all sub-corners within the edges.
          subCorners[quadrant.dx][quadrant.dy] = 1;
          subCorners[quadrant.dx + 1][quadrant.dy] = 1;
          subCorners[quadrant.dx][quadrant.dy + 1] = 1;
          subCorners[quadrant.dx + 1][quadrant.dy + 1] = 1;
          quadrant.processed = true;
          quadrantAddedOrSubdivided = true;
        } else if (halfSize <= minSize) {
          ghostCells++;
          quadrant.processed = true;
          quadrant.subdivided = true;
        }
      }
    }

    // Check for quadrants to subdivide unless we already hit min size.
    if (cell.size > minSize) {
      for (const quadrant of quadrants) {
        // No more subdivision if it's already processed.
        if (quadrant.processed) {
          continue;
        }

        const subDivisionResult = subdivideCellRecursive(
          {
            vertex: quadrant.center,
            size: halfSize,
          },
          kdTree,
          minSize,
          [
            subCorners[quadrant.dx][quadrant.dy] === 1,
            subCorners[quadrant.dx + 1][quadrant.dy] === 1,
            subCorners[quadrant.dx][quadrant.dy + 1] === 1,
            subCorners[quadrant.dx + 1][quadrant.dy + 1] === 1,
          ],
        );

        ghostCells += subDivisionResult.ghostCells;
        // Empty result means no subdivision happened. But we can always retry subdividing it later after we subdivided nearby quadrants.
        if (subDivisionResult.cells.length === 0) {
          continue;
        }

        result.push(...subDivisionResult.cells);

        // The corners return from the subdivision result shows which corners we were able subdivide
        // into. If the corners were added then we can safely mark them as being within the edge boundary.

        // Bottom left
        subCorners[quadrant.dx][quadrant.dy] = subDivisionResult.subCorners[0][0];
        // Bottom right
        subCorners[quadrant.dx + 1][quadrant.dy] = subDivisionResult.subCorners[2][0];
        // Top left
        subCorners[quadrant.dx][quadrant.dy + 1] = subDivisionResult.subCorners[0][2];
        // Top right
        subCorners[quadrant.dx + 1][quadrant.dy + 1] = subDivisionResult.subCorners[2][2];

        quadrant.processed = true;
        quadrant.subdivided = true;
        quadrantAddedOrSubdivided = true;
      }
    }
  }

  // If all the quadrants have been processed (added) and none of them are subdivided we can return the original cell.
  if (quadrants.every((x) => x.processed && !x.subdivided)) {
    return { cells: [cell], subCorners, ghostCells };
  }

  return { cells: result, subCorners, ghostCells };
}
